import { useQuery } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUpRight, Loader2 } from 'lucide-react';
import { gauntlet } from '../lib/api';
import { cx, formatAmount, shortenAddress, timeAgo } from '../lib/format';

interface PendingTransfer {
  id: number;
  txid: string;
  direction: 'in' | 'out' | 'self';
  from_address: string | null;
  to_address: string | null;
  amount: string;
  symbol: string;
  decimals: number;
  chain_id: string;
  confirmations: number;
  timestamp: number;
}

/**
 * Money the chain has seen but not yet confirmed.
 *
 * A wallet that shows only settled funds leaves you refreshing and wondering
 * whether a payment was ever sent. The mempool already knows, so there is no
 * reason to withhold it — an incoming payment appears here within about a
 * minute of being broadcast, long before it is spendable.
 *
 * Polls quickly, but only while something is actually moving: the endpoint
 * reads the local database, and the network work behind it is a separate job
 * that touches a handful of addresses rather than all of them.
 */
export function InFlight({ compact = false }: { compact?: boolean }) {
  const { data } = useQuery({
    queryKey: ['pending'],
    queryFn: () => gauntlet.pending(),
    refetchInterval: (query) => (query.state.data?.inFlight ? 8_000 : 30_000),
  });

  const transfers = (data?.transfers ?? []) as unknown as PendingTransfer[];
  const unconfirmed = data?.unconfirmed ?? [];

  if (transfers.length === 0 && unconfirmed.length === 0) return null;

  const incoming = transfers.filter((t) => t.direction === 'in');
  const outgoing = transfers.filter((t) => t.direction === 'out');

  return (
    <div className="overflow-hidden rounded-xl border border-brass/30 bg-brass-wash">
      <div className="flex items-center gap-2 border-b border-brass/20 px-4 py-2.5">
        <Loader2 size={14} className="animate-spin text-brass" />
        <span className="text-[13px] font-medium text-brass">
          {incoming.length > 0 && outgoing.length > 0
            ? 'Money moving in and out'
            : incoming.length > 0
              ? `${incoming.length} payment${incoming.length === 1 ? '' : 's'} arriving`
              : `${outgoing.length} payment${outgoing.length === 1 ? '' : 's'} on the way`}
        </span>
        <span className="ml-auto text-[11px] text-ink-faint">not yet confirmed</span>
      </div>

      <ul className="divide-y divide-brass/15">
        {transfers.slice(0, compact ? 3 : 12).map((transfer) => {
          const arriving = transfer.direction === 'in';
          const counterparty = arriving ? transfer.from_address : transfer.to_address;
          return (
            <li key={transfer.id} className="flex items-center gap-3 px-4 py-2.5">
              <div
                className={cx(
                  'flex size-7 shrink-0 items-center justify-center rounded-full border',
                  arriving ? 'border-gain/30 bg-gain/10 text-gain' : 'border-hairline text-ink-dim',
                )}
              >
                {arriving ? <ArrowDownLeft size={13} /> : <ArrowUpRight size={13} />}
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[13px]">
                  {arriving ? 'Incoming' : 'Outgoing'}
                  <span className="text-ink-faint">
                    {counterparty ? ` · ${arriving ? 'from' : 'to'} ${shortenAddress(counterparty, 6, 5)}` : ''}
                  </span>
                </p>
                <p className="text-[11px] text-ink-faint">
                  seen {timeAgo(transfer.timestamp)} · waiting for the first confirmation
                </p>
              </div>

              <span className={cx('num shrink-0 text-sm font-medium', arriving && 'text-gain')}>
                {arriving ? '+' : '−'}
                {formatAmount(transfer.amount, transfer.decimals ?? 8)} {transfer.symbol}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Balance the indexer reports as unconfirmed but for which we have no
          transfer row yet — value can show up here first. */}
      {unconfirmed.length > 0 && (
        <div className="border-t border-brass/20 px-4 py-2">
          <p className="text-[11px] text-ink-faint">
            Unconfirmed balance:{' '}
            {unconfirmed
              .map(
                (u) =>
                  `${formatAmount(String(u.amount ?? '0'), Number(u.decimals ?? 8))} ${String(u.symbol)}`,
              )
              .join(' · ')}
          </p>
        </div>
      )}
    </div>
  );
}
